/**
 * Where a drop-up anchored to the composer actually fits (2026-09-17).
 *
 * Owner report: "when the user dynamically goes from desktop view to mobile
 * view, in between there, the actual pop up saved prompt area actually is cut
 * off really bad."
 *
 * Two surfaces drop up from the composer — the saved-prompts panel and the `/`
 * typeahead — and both placed themselves with the same expression:
 *
 *     left: Math.max(8, anchor.left)
 *
 * which clamps the LEFT edge and says nothing about the right one. The trigger
 * sits near the right of the composer, so on a wide window `anchor.left` is a
 * large number; the surface is a fixed 420px; and `left + 420` walks off the
 * viewport the moment the window is narrower than the anchor position assumed.
 * Nothing about that is a phone-only bug — it is worst in the BAND between the
 * two layouts, because the panel variant is still in use above the 820px phone
 * breakpoint while the window has already become too narrow to hold a 420px
 * panel at that offset.
 *
 * The second half of the same report is the resize itself. The anchor is
 * measured once when the surface opens, deliberately — re-anchoring mid-SCROLL
 * is jitter, and that reasoning is untouched here. But a window resize is not
 * scroll: the trigger genuinely moved, and a placement computed against the old
 * width is stale in a way the user can see. So the placement is a function of
 * the LIVE viewport rather than a value frozen at open, and clamping happens on
 * every render. Even with a stale anchor the surface is now guaranteed to be on
 * screen; the caller re-measures the anchor on resize so it is also pointing at
 * the right place.
 *
 * PURE, and shared, for the reason the repo keeps relearning: a rule written
 * twice drifts. `composerControlsLayout.ts` next door is the same shape — pure
 * layout predicates, exhaustively testable at every width in both directions,
 * and deliberately free of any Firebase import (the 2026-09-11 lesson: a module
 * pulled into the composer must not drag `firebase/auth`, which touches global
 * fetch at import time, into the chat-isolation suites).
 */

import { useEffect, useState } from 'react';

/** The gap kept between a drop-up and every viewport edge. */
export const DROP_UP_EDGE_GAP = 8;

/** How far above the anchor's top edge the surface sits. */
export const DROP_UP_ANCHOR_GAP = 6;

export interface DropUpAnchor {
  /** The trigger's left edge, viewport-relative (a getBoundingClientRect x). */
  left: number;
  /** The trigger's top edge, viewport-relative. */
  top: number;
}

export interface DropUpViewport {
  width: number;
  height: number;
}

export interface DropUpPlacement {
  left: number;
  bottom: number;
  width: number;
}

function finite(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}

/**
 * Place a drop-up so it is fully on screen, at any viewport and any anchor.
 *
 * `preferredWidth` is what the surface wants (420 for both of ours today);
 * `minWidth` is the narrowest it stays useful at. A viewport too narrow to
 * honour `minWidth` gets the viewport's own width minus the gaps — being a
 * little cramped beats hanging off the edge, which is the bug this exists for.
 *
 * Returns today's un-clamped behaviour for an unmeasurable viewport (a server
 * render, jsdom before layout): a zero-width window is not a real one, and
 * inventing a clamp from it would place every surface at the left gutter.
 */
export function dropUpPlacement(args: {
  anchor: DropUpAnchor;
  viewport: DropUpViewport;
  preferredWidth: number;
  minWidth?: number;
}): DropUpPlacement {
  const gap = DROP_UP_EDGE_GAP;
  const preferred = finite(args.preferredWidth) && args.preferredWidth > 0 ? args.preferredWidth : 0;
  const min = finite(args.minWidth) && args.minWidth > 0 ? args.minWidth : 0;
  const anchorLeft = finite(args.anchor?.left) ? args.anchor.left : gap;
  const anchorTop = finite(args.anchor?.top) ? args.anchor.top : 0;

  const vw = args.viewport?.width;
  const vh = args.viewport?.height;

  // No measurable viewport — clamp the near edges only, exactly as before.
  if (!finite(vw) || vw <= 0) {
    return {
      left: Math.max(gap, anchorLeft),
      bottom: finite(vh) && vh > 0 ? Math.max(gap, vh - anchorTop + DROP_UP_ANCHOR_GAP) : gap,
      width: Math.max(preferred, min),
    };
  }

  // The widest this surface may be here. `available` can go negative on an
  // absurdly small viewport, hence the floor: a width is never below zero.
  const available = Math.max(0, vw - gap * 2);
  const width = Math.max(0, Math.min(Math.max(preferred, min), available) || available);

  // BOTH edges. `rightMost` is where the surface's left may sit at the furthest
  // right and still clear the gap; it can fall below `gap` when the surface is
  // as wide as the viewport, so the lower bound wins via Math.max.
  const rightMost = vw - width - gap;
  const left = Math.min(Math.max(anchorLeft, gap), Math.max(gap, rightMost));

  let bottom = gap;
  if (finite(vh) && vh > 0) {
    // Above the anchor, never below the bottom gap and never pushed so high
    // that the surface's own bottom edge leaves the top of the screen.
    bottom = Math.min(Math.max(vh - anchorTop + DROP_UP_ANCHOR_GAP, gap), Math.max(gap, vh - gap));
  }

  return { left, bottom, width };
}

/**
 * Track the viewport while a drop-up is open, so its placement follows a
 * resize. Inert (and it attaches no listener at all) while `active` is false —
 * a closed surface has no geometry to keep up to date.
 *
 * Listens to resize and orientationchange, and to `visualViewport` where the
 * browser has one, which is what actually changes when a phone keyboard opens.
 * NOT to scroll: re-anchoring mid-scroll is jitter, and that is the one piece
 * of the original "measure once" reasoning worth keeping.
 */
export function useDropUpViewport(active: boolean): DropUpViewport {
  const [viewport, setViewport] = useState<DropUpViewport>(() => currentViewport());

  useEffect(() => {
    if (!active || typeof window === 'undefined') return undefined;
    const sync = () => {
      setViewport((prev) => {
        const next = currentViewport();
        return prev.width === next.width && prev.height === next.height ? prev : next;
      });
    };
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    const vv = (window as unknown as { visualViewport?: EventTarget }).visualViewport;
    vv?.addEventListener?.('resize', sync);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
      vv?.removeEventListener?.('resize', sync);
    };
  }, [active]);

  return viewport;
}

/**
 * The live viewport, or a zero-size one where there is no window.
 *
 * Kept here so both callers read the same thing and neither has to remember
 * that `visualViewport` is the honest width on a phone with a keyboard up.
 */
export function currentViewport(): DropUpViewport {
  if (typeof window === 'undefined') return { width: 0, height: 0 };
  const vv = (window as unknown as { visualViewport?: { width: number; height: number } }).visualViewport;
  const width = finite(vv?.width) && (vv as { width: number }).width > 0
    ? (vv as { width: number }).width
    : window.innerWidth;
  const height = finite(vv?.height) && (vv as { height: number }).height > 0
    ? (vv as { height: number }).height
    : window.innerHeight;
  return {
    width: finite(width) ? width : 0,
    height: finite(height) ? height : 0,
  };
}
